import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Button, Stack, IconButton, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNotify } from 'react-admin';
import authFetch from '../utils/authFetch';
import { NAVY, inputSx } from '../styles/ds';

const EMPTY = { title: '', description: '', due_date: '', priority: 'medium', assigned_to: '', entity_type: '', entity_id: '' };

const TaskDialog = ({ open, task, onClose, onSaved, defaultEntity }) => {
  const notify = useNotify();
  const [form,    setForm]    = useState(EMPTY);
  const [users,   setUsers]   = useState([]);
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    if (!open) return;
    authFetch('/users?_start=0&_end=100')
      .then(r => r.json())
      .then(d => setUsers(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (task) {
      setForm({
        title:       task.title       || '',
        description: task.description || '',
        due_date:    task.due_date    || '',
        priority:    task.priority    || 'medium',
        assigned_to: task.assigned_to ?? '',
        entity_type: task.entity_type || '',
        entity_id:   task.entity_id   || '',
      });
    } else {
      setForm({
        ...EMPTY,
        entity_type: defaultEntity?.type || '',
        entity_id:   defaultEntity?.id   || '',
      });
    }
  }, [task, open, defaultEntity]);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    if (!form.title.trim()) { notify('כותרת חובה', { type: 'warning' }); return; }
    setSaving(true);
    try {
      const body = {
        title:       form.title.trim(),
        description: form.description || null,
        due_date:    form.due_date    || null,
        priority:    form.priority,
        assigned_to: form.assigned_to || null,
        entity_type: form.entity_type || null,
        entity_id:   form.entity_id   || null,
      };
      const url    = task ? `/tasks/${task.id}` : '/tasks';
      const method = task ? 'PUT' : 'POST';
      const r = await authFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || 'שגיאה'); }
      notify(task ? 'המשימה עודכנה' : 'המשימה נוצרה', { type: 'success' });
      onSaved();
    } catch (e) {
      notify(e.message, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth dir="rtl">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, color: NAVY }}>
        {task ? 'ערוך משימה' : 'משימה חדשה'}
        <IconButton size="small" onClick={onClose} aria-label="סגור"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} pt={1}>
          <TextField label="כותרת *" value={form.title} onChange={set('title')} fullWidth sx={inputSx} autoFocus />
          <TextField label="תיאור" value={form.description} onChange={set('description')} fullWidth multiline rows={2} sx={inputSx} />
          <Stack className="crm-filter-row" direction="row" spacing={2}>
            <TextField label="תאריך יעד" type="date" value={form.due_date} onChange={set('due_date')}
              fullWidth sx={inputSx} InputLabelProps={{ shrink: true }} />
            <TextField label="עדיפות" select value={form.priority} onChange={set('priority')} fullWidth sx={inputSx}>
              <MenuItem value="high">גבוהה</MenuItem>
              <MenuItem value="medium">בינונית</MenuItem>
              <MenuItem value="low">נמוכה</MenuItem>
            </TextField>
          </Stack>
          <TextField label="מוקצה ל" select value={form.assigned_to} onChange={set('assigned_to')} fullWidth sx={inputSx}>
            <MenuItem value="">— לא מוקצה —</MenuItem>
            {users.map(u => <MenuItem key={u.id} value={u.id}>{u.username}</MenuItem>)}
          </TextField>
          <Stack className="crm-filter-row" direction="row" spacing={2}>
            <TextField label="קשור ל" select value={form.entity_type} onChange={e => setForm(f => ({ ...f, entity_type: e.target.value, entity_id: '' }))}
              fullWidth sx={inputSx}>
              <MenuItem value="">— כללי —</MenuItem>
              <MenuItem value="customer">לקוח</MenuItem>
              <MenuItem value="order">הזמנה</MenuItem>
              <MenuItem value="lead">ליד</MenuItem>
            </TextField>
            {form.entity_type && (
              <TextField label={form.entity_type === 'customer' ? 'מספר לקוח' : form.entity_type === 'order' ? 'מספר הזמנה' : 'מספר ליד'}
                type="number" value={form.entity_id} onChange={set('entity_id')} fullWidth sx={inputSx} />
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: '#666' }}>ביטול</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ backgroundColor: NAVY, borderRadius: 2, '&:hover': { backgroundColor: '#162d5a' } }}>
          {task ? 'שמור שינויים' : 'צור משימה'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskDialog;
